import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  AutoAwesome,
  CheckCircle,
  Warning,
  School,
  Rule,
  Pattern as PatternIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Build,
} from '@mui/icons-material';
import { PageHeader, Loading, CandidateIdsInput } from './common';
import { apiService } from '../services/api';

const normalizeGuideline = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return Object.entries(val)
      .map(([k, v]) => `${k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:\n${typeof v === 'string' ? v : JSON.stringify(v, null, 2)}`)
      .join('\n\n');
  }
  return String(val);
};

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

const ScorePatternDiscovery = () => {
  const [tabValue, setTabValue] = useState(0);

  // ── Shared state ──
  const [aspects, setAspects] = useState([]);
  const [candidateIds, setCandidateIds] = useState('');

  // ── Single Discovery state ──
  const [selectedAspect, setSelectedAspect] = useState('');
  const [targetScore, setTargetScore] = useState('');
  const [discoveryId, setDiscoveryId] = useState(null);
  const [discoveryStatus, setDiscoveryStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guideline/Example creation
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createType, setCreateType] = useState('guideline');
  const [createForm, setCreateForm] = useState({ name: '', aspect: '', guideline_text: '', few_shot_example_text: '', is_active: true });
  const [createLoading, setCreateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Few-Shot Builder state ──
  const [builderAspects, setBuilderAspects] = useState([]);
  const [builderTargetScores, setBuilderTargetScores] = useState('0, 0.5, 1, 1.5, 2');
  const [builderAutoSave, setBuilderAutoSave] = useState(true);
  const [builderGroupId, setBuilderGroupId] = useState(null);
  const [builderStatus, setBuilderStatus] = useState(null);
  const [builderPolling, setBuilderPolling] = useState(false);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderError, setBuilderError] = useState(null);
  const [expandedDiscovery, setExpandedDiscovery] = useState(null);

  // ── History state ──
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedBuilderRun, setSelectedBuilderRun] = useState(null);
  const [builderRunDetails, setBuilderRunDetails] = useState(null);

  const pollingRef = useRef(null);
  const builderPollingRef = useRef(null);

  const loadAspects = useCallback(async () => {
    try {
      const res = await apiService.aspects.list(7);
      setAspects(res.data || []);
    } catch (err) {
      console.error('Failed to load aspects:', err);
    }
  }, []);

  useEffect(() => {
    loadAspects();
  }, [loadAspects]);

  // ── Load history when Builder tab is selected ──
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiService.fewShotBuilder.list();
      setHistory(res.data || { builder_runs: [], standalone_discoveries: [] });
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleViewBuilderRun = useCallback(async (bulkGroupId) => {
    try {
      const res = await apiService.fewShotBuilder.getStatus(bulkGroupId);
      setBuilderRunDetails(res.data);
      setSelectedBuilderRun(bulkGroupId);
    } catch (err) {
      setBuilderError(err.response?.data?.error || 'Failed to load builder run details.');
    }
  }, []);

  useEffect(() => {
    if (tabValue === 2 && !history) {
      loadHistory();
    }
  }, [tabValue, history, loadHistory]);

  // ── Single Discovery polling ──
  useEffect(() => {
    if (!polling || !discoveryId) return;
    pollingRef.current = setInterval(async () => {
      try {
        const res = await apiService.scorePatternDiscovery.getStatus(discoveryId);
        setDiscoveryStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setPolling(false);
          clearInterval(pollingRef.current);
        }
      } catch (err) {
        setPolling(false);
        clearInterval(pollingRef.current);
        setError('Failed to check discovery status: ' + (err.response?.data?.error || err.message));
      }
    }, 5000);
    return () => clearInterval(pollingRef.current);
  }, [polling, discoveryId]);

  // ── Builder polling ──
  useEffect(() => {
    if (!builderPolling || !builderGroupId) return;
    builderPollingRef.current = setInterval(async () => {
      try {
        const res = await apiService.fewShotBuilder.getStatus(builderGroupId);
        setBuilderStatus(res.data);
        const allDone = res.data.total > 0 && res.data.running === 0 && res.data.queued === 0;
        if (allDone) {
          setBuilderPolling(false);
          clearInterval(builderPollingRef.current);
        }
      } catch (err) {
        setBuilderPolling(false);
        clearInterval(builderPollingRef.current);
        setBuilderError('Failed to check builder status: ' + (err.response?.data?.error || err.message));
      }
    }, 5000);
    return () => clearInterval(builderPollingRef.current);
  }, [builderPolling, builderGroupId]);

  const MAX_CANDIDATES = 30;

  const handleStartDiscovery = async () => {
    if (!selectedAspect || !targetScore || !candidateIds.trim()) {
      setError('Aspect, target score, and candidate IDs are required.');
      return;
    }

    let ids = candidateIds.trim().split(/[\n,\s]+/).map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    if (ids.length === 0) {
      setError('No valid candidate IDs found.');
      return;
    }

    if (ids.length > MAX_CANDIDATES) {
      if (!window.confirm(`Only the first ${MAX_CANDIDATES} candidates will be used (you provided ${ids.length}). Continue?`)) {
        return;
      }
      ids = ids.slice(0, MAX_CANDIDATES);
    }

    const aspect = aspects.find(a => a.id === Number(selectedAspect));
    if (!aspect) {
      setError('Invalid aspect selected.');
      return;
    }

    setLoading(true);
    setError(null);
    setDiscoveryStatus(null);

    try {
      const res = await apiService.scorePatternDiscovery.start({
        control_sum_id: aspect.control_sum_id,
        target_score: parseFloat(targetScore),
        candidate_ids: ids,
      });
      setDiscoveryId(res.data.discovery_id);
      setPolling(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start discovery.');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (type, overrideText = null, overrideAspectId = null, overrideAspectName = null, overrideTargetScore = null) => {
    const text = overrideText || discoveryStatus?.synthesized_guideline || '';
    if (!text) {
      setSnackbar({ open: true, message: 'No synthesized guideline available.', severity: 'error' });
      return;
    }
    const aspectId = overrideAspectId || selectedAspect;
    const aspectName = overrideAspectName || aspects.find(a => a.id === Number(aspectId))?.name || '';
    const scoreLabel = overrideTargetScore !== null ? overrideTargetScore : targetScore;
    setCreateType(type);
    setCreateForm({
      name: `${type === 'guideline' ? 'Guideline' : 'Example'}: ${aspectName} (Score ${scoreLabel})`,
      aspect: Number(aspectId),
      guideline_text: type === 'guideline' ? text : '',
      few_shot_example_text: type === 'fewShot' ? text : '',
      is_active: true,
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!createForm.aspect) {
      setSnackbar({ open: true, message: 'Please select an aspect.', severity: 'error' });
      return;
    }
    setCreateLoading(true);
    try {
      if (createType === 'guideline') {
        await apiService.guidelines.create({
          name: createForm.name,
          aspect: Number(createForm.aspect),
          guideline_text: createForm.guideline_text,
          is_active: createForm.is_active,
        });
        setSnackbar({ open: true, message: 'Guideline created successfully!', severity: 'success' });
      } else {
        await apiService.fewShotExamples.create({
          name: createForm.name,
          aspect: Number(createForm.aspect),
          few_shot_example_text: createForm.few_shot_example_text,
          is_active: createForm.is_active,
        });
        setSnackbar({ open: true, message: 'Few-shot example created successfully!', severity: 'success' });
      }
      setCreateDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || err.response?.data?.detail || 'Failed to create.', severity: 'error' });
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Builder: Start ──
  const handleStartBuilder = async () => {
    if (builderAspects.length === 0) {
      setBuilderError('Select at least one aspect.');
      return;
    }
    if (!builderTargetScores.trim()) {
      setBuilderError('Enter at least one target score.');
      return;
    }
    if (!candidateIds.trim()) {
      setBuilderError('Candidate IDs are required.');
      return;
    }

    let ids = candidateIds.trim().split(/[\n,\s]+/).map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    if (ids.length === 0) {
      setBuilderError('No valid candidate IDs found.');
      return;
    }

    const totalCombinations = builderAspects.length * builderTargetScores.split(',').filter(s => s.trim()).length;

    setBuilderLoading(true);
    setBuilderError(null);
    setBuilderStatus(null);

    try {
      const res = await apiService.fewShotBuilder.start({
        aspect_ids: builderAspects.map(id => Number(id)),
        target_scores: builderTargetScores,
        candidate_ids: ids,
        auto_save_few_shot: builderAutoSave,
      });
      setBuilderGroupId(res.data.bulk_group_id);
      setBuilderPolling(true);
      setSnackbar({ open: true, message: `Builder started: ${totalCombinations} discovery tasks queued.`, severity: 'info' });
    } catch (err) {
      setBuilderError(err.response?.data?.error || 'Failed to start builder.');
    } finally {
      setBuilderLoading(false);
    }
  };

  const matchedCount = discoveryStatus?.individual_explanations?.filter(e => !e.error).length || 0;
  const mismatchCount = discoveryStatus?.mismatch_candidate_ids?.length || 0;

  // ── Builder status helpers ──
  const builderProgress = builderStatus ? ((builderStatus.completed + builderStatus.failed) / builderStatus.total) * 100 : 0;

  const statusColor = (s) => {
    if (s === 'completed') return 'success';
    if (s === 'failed') return 'error';
    if (s === 'running') return 'warning';
    return 'default';
  };

  return (
    <Box>
      <PageHeader
        title="Score Pattern Discovery"
        subtitle="Find common scoring patterns across candidates and synthesize reusable few-shot examples"
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Single Discovery" icon={<PatternIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Few-Shot Builder" icon={<Build fontSize="small" />} iconPosition="start" />
          <Tab label="History" icon={<Search fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ── Tab 1: Single Discovery ── */}
      <TabPanel value={tabValue} index={0}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Discover Score Patterns
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an aspect and target score. Upload or paste candidate IDs (one per line, max {MAX_CANDIDATES}). The system will find candidates
              with ground truth scores near that target, explain each, and synthesize a common scoring pattern.
            </Typography>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Aspect</InputLabel>
                  <Select value={selectedAspect} onChange={(e) => setSelectedAspect(e.target.value)} label="Aspect">
                    {aspects.map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name} (CS: {a.control_sum_id})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Target Score"
                  value={targetScore}
                  onChange={(e) => setTargetScore(e.target.value)}
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CandidateIdsInput
                  value={candidateIds}
                  onChange={setCandidateIds}
                  label="Candidate IDs (one per line)"
                  helperText=""
                  minRows={1}
                  maxRows={6}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  onClick={handleStartDiscovery}
                  disabled={loading || polling || !selectedAspect || !targetScore}
                  startIcon={loading || polling ? <CircularProgress size={16} /> : <PatternIcon />}
                  fullWidth
                  size="small"
                  sx={{ height: 40 }}
                >
                  {polling ? 'Discovering...' : loading ? 'Starting...' : 'Discover'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {(loading || polling) && !discoveryStatus && (
          <Loading message="Starting score pattern discovery..." size="large" />
        )}

        {discoveryStatus && (
          <>
            {/* Status */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={discoveryStatus.status}
                      color={discoveryStatus.status === 'completed' ? 'success' : discoveryStatus.status === 'failed' ? 'error' : 'warning'}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Candidates Explained</Typography>
                    <Typography variant="h4" fontWeight={700} color="primary.main">{matchedCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Score Mismatches</Typography>
                    <Typography variant="h4" fontWeight={700} color={mismatchCount > 0 ? 'warning.main' : 'success.main'}>{mismatchCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Target Score</Typography>
                    <Typography variant="h4" fontWeight={700}>{discoveryStatus.target_score}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Synthesized Guideline */}
            {discoveryStatus.synthesized_guideline && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>
                      <AutoAwesome sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                      Synthesized Scoring Guideline
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<Rule fontSize="small" />} onClick={() => openCreateDialog('guideline')}>
                        Create Guideline
                      </Button>
                      <Button size="small" variant="outlined" color="secondary" startIcon={<School fontSize="small" />} onClick={() => openCreateDialog('fewShot')}>
                        Create Example
                      </Button>
                    </Box>
                  </Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {normalizeGuideline(discoveryStatus.synthesized_guideline)}
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            )}

            {discoveryStatus.error_message && (
              <Alert severity="error" sx={{ mb: 3 }}>{discoveryStatus.error_message}</Alert>
            )}

            {/* Individual Explanations */}
            {discoveryStatus.individual_explanations?.length > 0 && (
              <Accordion defaultExpanded={false} sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Individual Explanations ({discoveryStatus.individual_explanations.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {discoveryStatus.individual_explanations.map((expl, idx) => (
                      <Card key={idx} variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              Candidate {expl.candidate_id}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip label={`GT: ${expl.gt_score}`} color="primary" size="small" />
                              {expl.error ? (
                                <Chip label="Error" color="error" size="small" />
                              ) : (
                                <Chip label={expl.rubric_level_identified || '—'} size="small" />
                              )}
                            </Box>
                          </Box>
                          {expl.error ? (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{expl.error}</Typography>
                          ) : (
                            <>
                              {expl.score_justification_summary && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  {expl.score_justification_summary}
                                </Typography>
                              )}
                              {expl.generalized_scoring_guideline && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                                    {normalizeGuideline(expl.generalized_scoring_guideline)}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </TabPanel>

      {/* ── Tab 2: Few-Shot Builder ── */}
      <TabPanel value={tabValue} index={1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              <Build sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Few-Shot Builder
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select multiple aspects and target scores. For each (aspect, score) combination, the system finds candidates matching
              that score (ranked by overall ground truth quality, top 30 used), runs GT Explainer, and synthesizes a few-shot example.
              {builderAutoSave ? ' Auto-saved examples are created as <strong>inactive</strong> — review and activate them in the Guidelines Manager.' : ''}
            </Typography>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Aspects</InputLabel>
                  <Select
                    multiple
                    value={builderAspects}
                    onChange={(e) => setBuilderAspects(e.target.value)}
                    label="Aspects"
                    renderValue={(selected) => `${selected.length} aspect${selected.length !== 1 ? 's' : ''} selected`}
                  >
                    {aspects.map(a => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name} (CS: {a.control_sum_id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Target Scores (comma-separated)"
                  value={builderTargetScores}
                  onChange={(e) => setBuilderTargetScores(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="0, 0.5, 1, 1.5"
                  helperText={builderAspects.length > 0 ? `${builderAspects.length} aspects × ${builderTargetScores.split(',').filter(s => s.trim()).length} scores = ${builderAspects.length * builderTargetScores.split(',').filter(s => s.trim()).length} discoveries` : ''}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <CandidateIdsInput
                  value={candidateIds}
                  onChange={setCandidateIds}
                  label="Candidate IDs (one per line)"
                  helperText=""
                  minRows={1}
                  maxRows={6}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={builderAutoSave}
                      onChange={(e) => setBuilderAutoSave(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Auto-save</Typography>}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleStartBuilder}
                  disabled={builderLoading || builderPolling || builderAspects.length === 0 || !builderTargetScores.trim() || !candidateIds.trim()}
                  startIcon={builderLoading || builderPolling ? <CircularProgress size={16} /> : <Build />}
                  fullWidth
                  size="small"
                  sx={{ height: 40 }}
                >
                  {builderPolling ? 'Building...' : builderLoading ? 'Starting...' : 'Build Few-Shots'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {builderError && <Alert severity="error" sx={{ mb: 3 }}>{builderError}</Alert>}

        {builderStatus && (
          <>
            {/* Progress */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Builder Progress
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {builderStatus.completed + builderStatus.failed} / {builderStatus.total} completed
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={builderProgress}
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Chip label={`${builderStatus.completed} Completed`} color="success" size="small" variant="outlined" />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip label={`${builderStatus.running} Running`} color="warning" size="small" variant="outlined" />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip label={`${builderStatus.queued} Queued`} color="default" size="small" variant="outlined" />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip label={`${builderStatus.failed} Failed`} color="error" size="small" variant="outlined" />
                  </Grid>
                </Grid>
                {builderStatus.auto_save_few_shot && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Auto-save is ON — synthesized examples are saved as <strong>inactive</strong> few-shot examples.
                    Review and activate them in the <strong>Guidelines Manager</strong>.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Discoveries Table */}
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Aspect</TableCell>
                    <TableCell align="center">Target Score</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Candidates</TableCell>
                    <TableCell align="center">Auto-saved</TableCell>
                    <TableCell align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {builderStatus.discoveries.map((d) => (
                    <React.Fragment key={d.discovery_id}>
                      <TableRow hover>
                        <TableCell>{d.aspect_name}</TableCell>
                        <TableCell align="center">{d.target_score}</TableCell>
                        <TableCell align="center">
                          <Chip label={d.status} color={statusColor(d.status)} size="small" />
                        </TableCell>
                        <TableCell align="center">{d.candidates_used}</TableCell>
                        <TableCell align="center">
                          {d.few_shot_auto_saved ? (
                            <CheckCircle color="success" fontSize="small" />
                          ) : d.status === 'completed' && !d.synthesized_guideline ? (
                            <Warning color="warning" fontSize="small" />
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          {d.synthesized_guideline || d.error_message ? (
                            <IconButton size="small" onClick={() => setExpandedDiscovery(expandedDiscovery === d.discovery_id ? null : d.discovery_id)}>
                              {expandedDiscovery === d.discovery_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expandedDiscovery === d.discovery_id} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 1 }}>
                              {d.error_message && (
                                <Alert severity="error" sx={{ mb: 1 }}>{d.error_message}</Alert>
                              )}
                              {d.synthesized_guideline && (
                                <>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Rule fontSize="small" />}
                                      onClick={() => openCreateDialog('guideline', d.synthesized_guideline, aspects.find(a => a.name === d.aspect_name)?.id, d.aspect_name, d.target_score)}
                                    >
                                      Create Guideline
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="secondary"
                                      startIcon={<School fontSize="small" />}
                                      onClick={() => openCreateDialog('fewShot', d.synthesized_guideline, aspects.find(a => a.name === d.aspect_name)?.id, d.aspect_name, d.target_score)}
                                    >
                                      Create Example
                                    </Button>
                                  </Box>
                                  <Alert severity="success">
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                                      {normalizeGuideline(d.synthesized_guideline)}
                                    </Typography>
                                  </Alert>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>

      {/* ── Tab 3: History ── */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Past Discoveries</Typography>
          <Button size="small" variant="outlined" onClick={loadHistory} disabled={historyLoading}>
            Refresh
          </Button>
        </Box>

        {historyLoading && !history && <Loading message="Loading history..." />}

        {history && (
          <>
            {/* Builder Runs */}
            {history.builder_runs?.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    <Build sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                    Builder Runs
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Aspects</TableCell>
                          <TableCell align="center">Total</TableCell>
                          <TableCell align="center">Completed</TableCell>
                          <TableCell align="center">Failed</TableCell>
                          <TableCell align="center">Auto-saved</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="center">Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.builder_runs.map((run) => (
                          <TableRow key={run.bulk_group_id} hover>
                            <TableCell>{run.aspects}</TableCell>
                            <TableCell align="center">{run.total}</TableCell>
                            <TableCell align="center">
                              <Chip label={run.completed} color="success" size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">
                              {run.failed > 0 ? (
                                <Chip label={run.failed} color="error" size="small" variant="outlined" />
                              ) : (
                                <Chip label="0" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {run.auto_save_few_shot ? <CheckCircle color="success" fontSize="small" /> : '—'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontSize="0.8rem">
                                {run.created_at ? new Date(run.created_at).toLocaleString() : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant={selectedBuilderRun === run.bulk_group_id ? "contained" : "outlined"}
                                onClick={() => handleViewBuilderRun(run.bulk_group_id)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Builder Run Details (when one is selected) */}
            {builderRunDetails && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Builder Run Details
                    </Typography>
                    <Button size="small" onClick={() => { setSelectedBuilderRun(null); setBuilderRunDetails(null); }}>
                      Close
                    </Button>
                  </Box>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item><Chip label={`${builderRunDetails.completed} Completed`} color="success" size="small" /></Grid>
                    <Grid item><Chip label={`${builderRunDetails.running} Running`} color="warning" size="small" /></Grid>
                    <Grid item><Chip label={`${builderRunDetails.failed} Failed`} color="error" size="small" /></Grid>
                  </Grid>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Aspect</TableCell>
                          <TableCell align="center">Target</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Candidates</TableCell>
                          <TableCell align="center">Saved</TableCell>
                          <TableCell align="center">Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {builderRunDetails.discoveries.map((d) => (
                          <React.Fragment key={d.discovery_id}>
                            <TableRow hover>
                              <TableCell>{d.aspect_name}</TableCell>
                              <TableCell align="center">{d.target_score}</TableCell>
                              <TableCell align="center">
                                <Chip label={d.status} color={statusColor(d.status)} size="small" />
                              </TableCell>
                              <TableCell align="center">{d.candidates_used}</TableCell>
                              <TableCell align="center">
                                {d.few_shot_auto_saved ? <CheckCircle color="success" fontSize="small" /> : null}
                              </TableCell>
                              <TableCell align="center">
                                {d.synthesized_guideline || d.error_message ? (
                                  <IconButton size="small" onClick={() => setExpandedDiscovery(expandedDiscovery === d.discovery_id ? null : d.discovery_id)}>
                                    {expandedDiscovery === d.discovery_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                  </IconButton>
                                ) : null}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                                <Collapse in={expandedDiscovery === d.discovery_id} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 2, px: 1 }}>
                                    {d.error_message && <Alert severity="error" sx={{ mb: 1 }}>{d.error_message}</Alert>}
                                    {d.synthesized_guideline && (
                                      <>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Rule fontSize="small" />}
                                            onClick={() => openCreateDialog('guideline', d.synthesized_guideline, aspects.find(a => a.name === d.aspect_name)?.id, d.aspect_name, d.target_score)}
                                          >
                                            Create Guideline
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<School fontSize="small" />}
                                            onClick={() => openCreateDialog('fewShot', d.synthesized_guideline, aspects.find(a => a.name === d.aspect_name)?.id, d.aspect_name, d.target_score)}
                                          >
                                            Create Example
                                          </Button>
                                        </Box>
                                        <Alert severity="success">
                                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                                            {normalizeGuideline(d.synthesized_guideline)}
                                          </Typography>
                                        </Alert>
                                      </>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Standalone Discoveries */}
            {history.standalone_discoveries?.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    <PatternIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 20 }} />
                    Single Discoveries
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Aspect</TableCell>
                          <TableCell align="center">Target</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="center">Candidates</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Preview</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.standalone_discoveries.map((d) => (
                          <TableRow key={d.discovery_id} hover>
                            <TableCell>{d.aspect_name}</TableCell>
                            <TableCell align="center">{d.target_score}</TableCell>
                            <TableCell align="center">
                              <Chip label={d.status} color={statusColor(d.status)} size="small" />
                            </TableCell>
                            <TableCell align="center">{d.candidates_used}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontSize="0.8rem">
                                {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontSize="0.75rem" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {d.synthesized_guideline || d.error_message || '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {(!history.builder_runs?.length && !history.standalone_discoveries?.length) && (
              <Alert severity="info">No past discoveries found.</Alert>
            )}
          </>
        )}
      </TabPanel>

      {/* Create Guideline / Few-Shot Example Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {createType === 'guideline' ? 'Create Guideline' : 'Create Few-Shot Example'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Aspect</InputLabel>
              <Select value={createForm.aspect} onChange={(e) => setCreateForm(prev => ({ ...prev, aspect: e.target.value }))} label="Aspect">
                {aspects.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.name} (ID: {a.control_sum_id})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Name"
              value={createForm.name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={<Switch checked={createForm.is_active} onChange={(e) => setCreateForm(prev => ({ ...prev, is_active: e.target.checked }))} />}
              label="Active"
            />
            {createType === 'guideline' ? (
              <TextField label="Guideline Text" value={createForm.guideline_text} onChange={(e) => setCreateForm(prev => ({ ...prev, guideline_text: e.target.value }))} fullWidth multiline minRows={6} maxRows={20} />
            ) : (
              <TextField label="Few-Shot Example Text" value={createForm.few_shot_example_text} onChange={(e) => setCreateForm(prev => ({ ...prev, few_shot_example_text: e.target.value }))} fullWidth multiline minRows={6} maxRows={20} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={createLoading || !createForm.aspect}>
            {createLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScorePatternDiscovery;